import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type Discount,
  useArchiveDiscountMutation,
  useCreateDiscountMutation,
  useGetDiscountQuery,
  useGetDiscountsQuery,
  useUpdateDiscountMutation,
} from '@/store/api/commerceP2cApi';
import { useToast } from '@/components/shared/Toast';

type DiscountKind = Discount['kind'];
type DiscountCategory = Discount['discountKind'];

interface DiscountForm {
  name: string;
  kind: DiscountKind;
  discountKind: DiscountCategory;
  status: 'draft' | 'active';
  percent: string;
  valueAmount: string;
  buy: string;
  get: string;
  selection: 'cheapest' | 'same-item';
  dateFrom: string;
  dateTo: string;
}

const emptyForm: DiscountForm = {
  name: '', kind: 'percent', discountKind: 'promo', status: 'active', percent: '',
  valueAmount: '', buy: '1', get: '1', selection: 'cheapest', dateFrom: '', dateTo: '',
};

function toDateInput(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function toIso(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function makeDiscountBody(form: DiscountForm): Partial<Discount> & Pick<Discount, 'name' | 'kind' | 'discountKind' | 'eligibility'> {
  return {
    name: form.name.trim(),
    kind: form.kind,
    discountKind: form.discountKind,
    status: form.status,
    eligibility: {},
    dateFrom: toIso(form.dateFrom),
    dateTo: toIso(form.dateTo),
    ...(form.kind === 'percent' ? { valueBps: Math.round(Number(form.percent) * 100) } : {}),
    ...(form.kind === 'fixed' ? { valueAmount: form.valueAmount.trim() } : {}),
    ...(form.kind === 'bogo' ? {
      bogoSpec: { buy: Number(form.buy), get: Number(form.get), selection: form.selection },
    } : {}),
  };
}

export function DiscountsPage() {
  const { discountId } = useParams<{ discountId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const createMode = discountId === 'new';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const { data: discounts, isLoading, isError, refetch } = useGetDiscountsQuery();
  const {
    data: discount,
    isLoading: isLoadingDiscount,
    isError: isDiscountError,
    refetch: refetchDiscount,
  } = useGetDiscountQuery(discountId ?? '', { skip: !discountId || createMode });
  const [createDiscount, { isLoading: isCreating }] = useCreateDiscountMutation();
  const [updateDiscount, { isLoading: isSaving }] = useUpdateDiscountMutation();
  const [archiveDiscount, { isLoading: isArchiving }] = useArchiveDiscountMutation();
  const [form, setForm] = useState<DiscountForm>(emptyForm);
  const [edit, setEdit] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [formErrorField, setFormErrorField] = useState('');

  useEffect(() => {
    if (!discount) return;
    setForm({
      name: discount.name,
      kind: discount.kind,
      discountKind: discount.discountKind,
      status: discount.status,
      percent: discount.valueBps === null || discount.valueBps === undefined ? '' : (discount.valueBps / 100).toFixed(2),
      valueAmount: discount.valueAmount ?? '',
      buy: String(discount.bogoSpec?.buy ?? 1),
      get: String(discount.bogoSpec?.get ?? 1),
      selection: discount.bogoSpec?.selection ?? 'cheapest',
      dateFrom: toDateInput(discount.dateFrom),
      dateTo: toDateInput(discount.dateTo),
    });
  }, [discount]);

  async function persist() {
    try {
      const body = makeDiscountBody(form);
      if (createMode) {
        const created = await createDiscount({ idempotencyKey: crypto.randomUUID(), body }).unwrap();
        toast.success(`Discount “${created.name}” created`);
        navigate(`/discounts/${created.id}`);
      } else if (discountId) {
        const updated = await updateDiscount({ id: discountId, idempotencyKey: crypto.randomUUID(), body }).unwrap();
        toast.success(`Discount “${updated.name}” saved`);
        setEdit(false);
        setStatusConfirmOpen(false);
      }
    } catch {
      toast.error('Could not save this discount. Your changes are still here; try again.');
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError('');
    setFormErrorField('');
    if (!form.name.trim()) {
      setFormError('Enter a discount name.');
      setFormErrorField('discount-name');
      return;
    }
    if (form.kind === 'percent' && (!form.percent.trim() || !Number.isFinite(Number(form.percent)) || Number(form.percent) < 0 || Number(form.percent) > 100)) {
      setFormError('Enter a percentage from 0 to 100.');
      setFormErrorField('discount-percent');
      return;
    }
    if (form.kind === 'fixed' && !/^\d+$/.test(form.valueAmount.trim())) {
      setFormError('Enter a non-negative integer string in minor units.');
      setFormErrorField('discount-amount');
      return;
    }
    if (form.dateFrom && form.dateTo && new Date(form.dateFrom) >= new Date(form.dateTo)) {
      setFormError('Valid until must be later than valid from.');
      setFormErrorField('discount-date-to');
      return;
    }
    if (!createMode && discount?.status === 'active' && form.status === 'draft') {
      setStatusConfirmOpen(true);
      return;
    }
    await persist();
  }

  async function archive() {
    if (!discountId || createMode || !discount) return;
    try {
      await archiveDiscount({ id: discountId, idempotencyKey: crypto.randomUUID() }).unwrap();
      toast.success(`Discount “${discount.name}” archived`);
      navigate('/discounts');
    } catch {
      toast.error(`Could not archive “${discount.name}”. Try again.`);
    }
  }

  if (createMode || discountId) {
    const formOpen = createMode || edit;
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/discounts')}>← Discounts</Button>
        <header>
          <h1 className="text-2xl font-bold">{createMode ? 'Create discount' : discount?.name ?? 'Discount details'}</h1>
          <p className="text-sm text-muted-foreground">Eligibility, amount units, and validity are shown only as returned by the service.</p>
        </header>
        {formOpen ? (
          <form className="max-w-2xl space-y-4" onSubmit={(event) => void save(event)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Discount name" id="discount-name"><Input id="discount-name" required maxLength={200} value={form.name} aria-invalid={formErrorField === 'discount-name'} aria-describedby={formErrorField === 'discount-name' ? 'discount-form-error' : undefined} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
              <Field label="Discount type" id="discount-kind">
                <select id="discount-kind" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value as DiscountKind })}>
                  <option value="percent">Percentage</option><option value="fixed">Fixed amount</option><option value="bogo">Buy one, get one</option>
                </select>
              </Field>
              <Field label="Category" id="discount-category">
                <select id="discount-category" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.discountKind} onChange={(event) => setForm({ ...form, discountKind: event.target.value as DiscountCategory })}>
                  {['employee', 'birthday', 'student', 'senior', 'military', 'promo', 'membership'].map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                </select>
              </Field>
              <Field label="Status" id="discount-status">
                <select id="discount-status" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as DiscountForm['status'] })}>
                  <option value="draft">Draft</option><option value="active">Active</option>
                </select>
              </Field>
              {form.kind === 'percent' && <Field label="Percentage (%)" id="discount-percent" help="Up to 100%; saved as basis points by the service."><Input id="discount-percent" type="number" min="0" max="100" step="0.01" required value={form.percent} aria-invalid={formErrorField === 'discount-percent'} aria-describedby={formErrorField === 'discount-percent' ? 'discount-form-error' : undefined} onChange={(event) => setForm({ ...form, percent: event.target.value })} /></Field>}
              {form.kind === 'fixed' && <Field label="Fixed amount (minor-unit string)" id="discount-amount" help="The contract returns no currency for fixed discount values; enter the non-negative integer string without converting it."><Input id="discount-amount" inputMode="numeric" pattern="\d+" required placeholder="e.g. 1250" value={form.valueAmount} aria-invalid={formErrorField === 'discount-amount'} aria-describedby={formErrorField === 'discount-amount' ? 'discount-form-error' : undefined} onChange={(event) => setForm({ ...form, valueAmount: event.target.value })} /></Field>}
              {form.kind === 'bogo' && <>
                <Field label="Buy quantity" id="discount-buy"><Input id="discount-buy" type="number" min="1" step="1" required value={form.buy} onChange={(event) => setForm({ ...form, buy: event.target.value })} /></Field>
                <Field label="Get quantity" id="discount-get"><Input id="discount-get" type="number" min="1" step="1" required value={form.get} onChange={(event) => setForm({ ...form, get: event.target.value })} /></Field>
                <Field label="Selection" id="discount-selection"><select id="discount-selection" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.selection} onChange={(event) => setForm({ ...form, selection: event.target.value as DiscountForm['selection'] })}><option value="cheapest">Cheapest item</option><option value="same-item">Same item</option></select></Field>
              </>}
              <Field label="Valid from" id="discount-date-from"><Input id="discount-date-from" type="datetime-local" value={form.dateFrom} onChange={(event) => setForm({ ...form, dateFrom: event.target.value })} /></Field>
              <Field label="Valid until" id="discount-date-to"><Input id="discount-date-to" type="datetime-local" value={form.dateTo} aria-invalid={formErrorField === 'discount-date-to'} aria-describedby={formErrorField === 'discount-date-to' ? 'discount-form-error' : undefined} onChange={(event) => setForm({ ...form, dateTo: event.target.value })} /></Field>
            </div>
            {formError && <p id="discount-form-error" role="alert" className="text-sm text-destructive">{formError}</p>}
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => createMode ? navigate('/discounts') : setEdit(false)}>Cancel</Button><Button type="submit" disabled={isCreating || isSaving}>{isCreating || isSaving ? 'Saving…' : createMode ? 'Create discount' : 'Save discount'}</Button></div>
            {!createMode && discount?.status === 'active' && <ConfirmDialog open={statusConfirmOpen} onOpenChange={setStatusConfirmOpen} title="Set active discount to draft?" description={`“${discount.name}” will no longer be active.`} confirmLabel="Set draft" variant="destructive" isLoading={isSaving} onConfirm={() => void persist()} />}
          </form>
        ) : isLoadingDiscount ? <LoadingRows label="Loading discount" columns={3} />
          : isDiscountError || !discount ? <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load this discount. <Button variant="outline" onClick={() => void refetchDiscount()}>Try again</Button></div>
            : <section className="max-w-2xl space-y-4">
              <dl className="grid gap-4 border-y py-4 sm:grid-cols-2">
                <Detail label="Discount ID" value={discount.id} /><Detail label="Name" value={discount.name} />
                <Detail label="Type" value={discount.kind} /><Detail label="Category" value={discount.discountKind} />
                <Detail label="Value" value={displayDiscountValue(discount)} /><Detail label="Status" value={discount.status} />
                <Detail label="Valid from" value={discount.dateFrom ?? '—'} /><Detail label="Valid until" value={discount.dateTo ?? '—'} />
                <Detail label="Eligibility returned by service" value={formatEligibility(discount.eligibility)} />
              </dl>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEdit(true)}>Edit discount</Button><Button variant="destructive" onClick={() => setArchiveOpen(true)}>Archive discount</Button></div>
              <ConfirmDialog open={archiveOpen} onOpenChange={setArchiveOpen} title="Archive discount?" description={`“${discount.name}” will be archived.`} confirmLabel="Archive discount" variant="destructive" isLoading={isArchiving} onConfirm={() => void archive()} />
            </section>}
      </div>
    );
  }

  const filtered = (discounts ?? []).filter((item) =>
    (status === 'all' || item.status === status) &&
    `${item.name} ${item.discountKind}`.toLowerCase().includes(search.trim().toLowerCase()),
  );
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3"><div><h1 className="text-2xl font-bold">Discounts</h1><p className="text-sm text-muted-foreground">The service returns non-archived discounts only. Names are the available display identifiers; no code field is returned.</p></div><Button asChild><Link to="/discounts/new">Create discount</Link></Button></header>
      <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
        <Field label="Search discounts" id="discount-search"><Input id="discount-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or category" /></Field>
        <Field label="Status" id="discount-filter-status"><select id="discount-filter-status" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All statuses</option><option value="draft">Draft</option><option value="active">Active</option></select></Field>
      </div>
      <section aria-busy={isLoading}>
        {isLoading ? <LoadingRows label="Loading discounts" columns={5} />
          : isError ? <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load discounts. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>
            : filtered.length === 0 ? <EmptyState title={search.trim() || status !== 'all' ? 'No matching discounts' : 'No discounts yet'} description={search.trim() || status !== 'all' ? 'Clear the filters to view all non-archived discounts.' : 'Create a discount to configure a supported promotion.'} action={search.trim() || status !== 'all' ? <Button variant="outline" onClick={() => { setSearch(''); setStatus('all'); }}>Clear filters</Button> : undefined} />
              : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Discount records"><table className="w-full min-w-[42rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Name</th><th scope="col" className="px-4 py-3">Type</th><th scope="col" className="px-4 py-3 text-right">Value</th><th scope="col" className="px-4 py-3">Validity</th><th scope="col" className="px-4 py-3">Status</th></tr></thead><tbody>{filtered.map((item) => <tr key={item.id} className="border-t"><td className="px-4 py-3"><Link className="font-medium text-primary underline-offset-4 hover:underline" to={`/discounts/${item.id}`}>{item.name}</Link><p className="text-xs text-muted-foreground">{item.discountKind}</p></td><td className="px-4 py-3">{item.kind}</td><td className="px-4 py-3 text-right tabular-nums">{displayDiscountValue(item)}</td><td className="px-4 py-3">{formatInterval(item.dateFrom, item.dateTo)}</td><td className="px-4 py-3">{item.status}</td></tr>)}</tbody></table></div>}
      </section>
    </div>
  );
}

function displayDiscountValue(discount: Discount): string {
  if (discount.kind === 'percent' && discount.valueBps !== null && discount.valueBps !== undefined) return `${(discount.valueBps / 100).toFixed(2)}%`;
  if (discount.kind === 'fixed' && discount.valueAmount !== null && discount.valueAmount !== undefined) return `${discount.valueAmount} minor units (currency not returned)`;
  if (discount.kind === 'bogo' && discount.bogoSpec) return `Buy ${discount.bogoSpec.buy}, get ${discount.bogoSpec.get}`;
  return '—';
}

function formatInterval(from?: string | null, to?: string | null): string {
  if (!from && !to) return 'No validity interval returned';
  return `${from ?? 'No start'} — ${to ?? 'No end'}`;
}

function formatEligibility(eligibility: Discount['eligibility']): string {
  if (!eligibility) return 'No eligibility fields returned.';
  const entries = Object.entries(eligibility).filter(([, values]) => Array.isArray(values) && values.length > 0);
  if (entries.length === 0) return 'No eligibility conditions returned.';
  return entries.map(([key, values]) => {
    const label = key.replace(/([A-Z])/g, ' $1').toLowerCase();
    return `${label}: ${(values as string[]).join(', ')}`;
  }).join('; ');
}

function Field({ label, id, help, children }: { label: string; id: string; help?: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}{help && <p className="text-xs text-muted-foreground">{help}</p>}</div>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 break-words text-sm">{value}</dd></div>;
}
